import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import SlidePanel from "../../../components/SlidePanel/SlidePanel";
import AppDropdown from "../../../components/AppDropdown/AppDropdown";
import * as hrmsService from "../../../services/hrmsService";
import EmployeeSelect from "../EmployeeSelect";
import { Field, FormFieldsLite } from "./workflowParts";
import { EmptyState, LoadState, PrimaryButton, SecondaryButton } from "../hrComponents";
import { cardStyle, iconBtnStyle, inputStyle, requestLabel, statusBadge, statusLabel, tdStyle, thStyle, useLoad } from "../hrUi";

// APR-*: who approves what. Each request type has a default workflow; extra ones can take over when conditions match
// (for example leave over 5 days goes to two levels). Up to five levels; each can escalate when it is not answered.
const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };

export const REQUEST_TYPES = ["LEAVE", "LEAVE_CANCEL", "COMP_OFF", "BANK_CHANGE", "PROFILE_CHANGE", "SEPARATION", "LETTER", "REGULARISATION", "SHIFT_SWAP", "OT"];
export const APPROVER_TYPES = [
  { label: "Reporting manager", value: "MANAGER" },
  { label: "Manager’s manager (level 2–5)", value: "MANAGER_N" },
  { label: "Department head", value: "DEPT_HEAD" },
  { label: "Site in-charge", value: "SITE_INCHARGE" },
  { label: "A named employee", value: "EMPLOYEE" },
  { label: "Anyone with a permission", value: "PERMISSION" },
];
const PERMISSIONS = [
  { label: "HR Admin", value: "HR_ADMIN" },
  { label: "HR Viewer", value: "HR_VIEWER" },
  { label: "Payroll Admin", value: "PAYROLL_ADMIN" },
  { label: "Finance Approver", value: "FINANCE_APPROVER" },
  { label: "Attendance Admin", value: "ATTENDANCE_ADMIN" },
];
const ESCALATIONS = [
  { label: "Do nothing", value: "NONE" },
  { label: "Escalate to the next level", value: "ESCALATE_NEXT" },
  { label: "Escalate to HR", value: "ESCALATE_HR" },
  { label: "Approve automatically", value: "AUTO_APPROVE" },
  { label: "Reject automatically", value: "AUTO_REJECT" },
];
// Conditions on ids (leave type, department, location) need raw ids the portal never sees, so the builder offers the numeric ones.
const CONDITION_FIELDS = [
  { label: "Number of days", value: "DAYS", kind: "number" },
  { label: "Amount", value: "AMOUNT", kind: "number" },
  { label: "Employment type", value: "EMPLOYMENT_TYPE", kind: "string" },
  { label: "Backdated", value: "BACKDATED", kind: "boolean" },
];
const OPS = { number: ["EQ", "NE", "GT", "GTE", "LT", "LTE"], string: ["EQ", "NE", "IN"], boolean: ["EQ", "NE"] };
const OP_LABEL = { EQ: "is", NE: "is not", GT: "more than", GTE: "at least", LT: "less than", LTE: "at most", IN: "is one of" };

const newLevel = () => ({ approver_type: "MANAGER", approver_ref: "", approver_employee_id: null, mode: "ANY", sla_hours: "", escalation: "NONE" });

// The builder's rows -> what the API takes.
export function buildWorkflowPayload(form) {
  return {
    name: form.name,
    request_type: form.request_type,
    is_default: !!form.is_default,
    priority: form.is_default ? undefined : Number(form.priority) || 100,
    levels: form.levels.map((l) => ({
      approver_type: l.approver_type,
      approver_ref: ["MANAGER_N", "PERMISSION"].includes(l.approver_type) ? String(l.approver_ref || "") : undefined,
      approver_employee_id: l.approver_type === "EMPLOYEE" ? l.approver_employee_id : undefined,
      mode: l.mode,
      sla_hours: l.sla_hours === "" || l.sla_hours === null ? undefined : Number(l.sla_hours),
      escalation: l.escalation,
    })),
    conditions: form.is_default ? [] : form.conditions.map((c) => ({ field: c.field, operator: c.operator, value: String(c.value) })),
  };
}

function WorkflowDrawer({ show, workflow, onHide, onDone }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!show) return;
    setForm(
      workflow
        ? {
            id: workflow.id,
            name: workflow.name,
            request_type: workflow.request_type,
            is_default: !!workflow.is_default,
            priority: workflow.priority,
            levels: workflow.levels.map((l) => ({ ...l, sla_hours: l.sla_hours ?? "", approver_ref: l.approver_ref ?? "" })),
            conditions: workflow.conditions.map((c) => ({ ...c })),
          }
        : { name: "", request_type: "LEAVE", is_default: false, priority: 100, levels: [newLevel()], conditions: [] }
    );
  }, [show, workflow]);
  if (!form) return null;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setLevel = (i, patch) => set({ levels: form.levels.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const setCond = (i, patch) => set({ conditions: form.conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)) });

  const save = async () => {
    setSaving(true);
    try {
      const res = await hrmsService.saveWorkflow(workflow?.id, buildWorkflowPayload(form));
      if (res?.success) {
        toast.success(res.message || "Saved");
        onDone();
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title={workflow ? "Edit workflow" : "New workflow"}
      width={640}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving || !form.name.trim()}>
            Save
          </PrimaryButton>
        </>
      }
    >
      <Field label="Name" required>
        <input className="form-control" style={inputStyle} maxLength={100} value={form.name} onChange={(e) => set({ name: e.target.value })} />
      </Field>
      <Field label="Request type" required>
        <AppDropdown value={form.request_type} options={REQUEST_TYPES.map((t) => ({ label: requestLabel(t), value: t }))} onChange={(e) => set({ request_type: e.value })} disabled={!!workflow} />
      </Field>
      <div className="form-check mb-3">
        <input id="wf-default" type="checkbox" className="form-check-input" checked={form.is_default} onChange={(e) => set({ is_default: e.target.checked })} />
        <label htmlFor="wf-default" className="form-check-label" style={{ fontSize: 14 }}>
          The default for this request type (used when no other workflow’s conditions match)
        </label>
      </div>

      {!form.is_default ? (
        <>
          <h6>When</h6>
          {form.conditions.map((c, i) => {
            const field = CONDITION_FIELDS.find((f) => f.value === c.field) || CONDITION_FIELDS[0];
            return (
              <div key={i} className="d-flex flex-wrap gap-2 align-items-center mb-2">
                <div style={{ width: 170 }}>
                  <AppDropdown value={c.field} options={CONDITION_FIELDS} onChange={(e) => setCond(i, { field: e.value, operator: "EQ", value: "" })} />
                </div>
                <div style={{ width: 140 }}>
                  <AppDropdown value={c.operator} options={OPS[field.kind].map((o) => ({ label: OP_LABEL[o], value: o }))} onChange={(e) => setCond(i, { operator: e.value })} />
                </div>
                {field.kind === "boolean" ? (
                  <div style={{ width: 100 }}>
                    <AppDropdown value={String(c.value || "true")} options={[{ label: "Yes", value: "true" }, { label: "No", value: "false" }]} onChange={(e) => setCond(i, { value: e.value })} />
                  </div>
                ) : (
                  <input className="form-control" style={{ ...inputStyle, width: 130 }} value={c.value} onChange={(e) => setCond(i, { value: e.target.value })} placeholder={field.kind === "number" ? "e.g. 5" : "e.g. CONTRACT"} />
                )}
                <button type="button" style={linkBtn} onClick={() => set({ conditions: form.conditions.filter((_, j) => j !== i) })}>
                  Remove
                </button>
              </div>
            );
          })}
          <button type="button" className="btn btn-outline-secondary btn-sm mb-3" onClick={() => set({ conditions: [...form.conditions, { field: "DAYS", operator: "GT", value: "" }] })}>
            Add condition
          </button>
          <Field label="Priority" hint="Lower numbers are checked first.">
            <input type="number" className="form-control" style={{ ...inputStyle, maxWidth: 140 }} min={1} value={form.priority} onChange={(e) => set({ priority: e.target.value })} />
          </Field>
        </>
      ) : null}

      <h6 className="mt-2">Approval levels</h6>
      {form.levels.map((l, i) => (
        <div key={i} style={{ ...cardStyle, padding: 14, marginBottom: 12, border: "1px solid #E5E7EB", boxShadow: "none" }}>
          <div className="d-flex justify-content-between mb-2">
            <strong>Level {i + 1}</strong>
            {form.levels.length > 1 ? (
              <button type="button" style={linkBtn} onClick={() => set({ levels: form.levels.filter((_, j) => j !== i) })}>
                Remove
              </button>
            ) : null}
          </div>
          <FormFieldsLite>
            <Field label="Approver">
              <AppDropdown value={l.approver_type} options={APPROVER_TYPES} onChange={(e) => setLevel(i, { approver_type: e.value })} />
            </Field>
            {l.approver_type === "MANAGER_N" ? (
              <Field label="Levels up (2–5)">
                <input type="number" className="form-control" style={inputStyle} min={2} max={5} value={l.approver_ref} onChange={(e) => setLevel(i, { approver_ref: e.target.value })} />
              </Field>
            ) : null}
            {l.approver_type === "PERMISSION" ? (
              <Field label="Permission">
                <AppDropdown value={l.approver_ref || null} options={PERMISSIONS} onChange={(e) => setLevel(i, { approver_ref: e.value })} placeholder="Choose" />
              </Field>
            ) : null}
            {l.approver_type === "EMPLOYEE" ? (
              <Field label="Employee">
                <EmployeeSelect value={l.approver_employee_id} onChange={(id) => setLevel(i, { approver_employee_id: id })} />
              </Field>
            ) : null}
            <Field label="When several approvers">
              <AppDropdown value={l.mode} options={[{ label: "Any one can approve", value: "ANY" }, { label: "All must approve", value: "ALL" }]} onChange={(e) => setLevel(i, { mode: e.value })} />
            </Field>
            <Field label="Reply within (hours)">
              <input type="number" className="form-control" style={inputStyle} min={1} value={l.sla_hours} onChange={(e) => setLevel(i, { sla_hours: e.target.value })} />
            </Field>
            <Field label="If not answered in time">
              <AppDropdown value={l.escalation} options={ESCALATIONS} onChange={(e) => setLevel(i, { escalation: e.value })} />
            </Field>
          </FormFieldsLite>
        </div>
      ))}
      {form.levels.length < 5 ? (
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => set({ levels: [...form.levels, newLevel()] })}>
          Add level
        </button>
      ) : null}
    </SlidePanel>
  );
}

const describeLevel = (l) => {
  const who = { MANAGER: "Manager", MANAGER_N: `Manager +${l.approver_ref}`, DEPT_HEAD: "Department head", SITE_INCHARGE: "Site in-charge", EMPLOYEE: "Named employee", PERMISSION: statusLabel(l.approver_ref) }[l.approver_type] || l.approver_type;
  return who;
};

export default function WorkflowsTab() {
  const { data, loading, error, reload } = useLoad(() => hrmsService.listWorkflows(), []);
  const [edit, setEdit] = useState(undefined); // undefined closed, null new, object edit
  const rows = data || [];

  const deactivate = async (w) => {
    const res = await hrmsService.deactivateWorkflow(w.id).catch((e) => ({ success: false, message: e.message }));
    if (res?.success) {
      toast.success(res.message || "Done");
      reload();
    } else toast.error(hrmsService.responseMessage(res));
  };

  return (
    <>
      <div className="mb-3">
        <PrimaryButton onClick={() => setEdit(null)}>New workflow</PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No workflows" text="Until you add one, requests have nobody to approve them. Create a default workflow for each request type you use." />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  {["Request", "Workflow", "Approval path", "Status", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((w) => (
                  <tr key={w.id}>
                    <td style={tdStyle}>{requestLabel(w.request_type)}</td>
                    <td style={tdStyle}>
                      {w.name}
                      {w.is_default ? <span style={{ ...statusBadge("PROBATION"), marginLeft: 8 }}>Default</span> : null}
                      {!w.is_default && w.conditions.length ? <div style={{ fontSize: 12, color: "#6B7280" }}>{w.conditions.map((c) => `${c.field.toLowerCase()} ${OP_LABEL[c.operator]} ${c.value}`).join(" and ")}</div> : null}
                    </td>
                    <td style={tdStyle}>{w.levels.map(describeLevel).join(" → ")}</td>
                    <td style={tdStyle}>
                      <span style={statusBadge(w.status)}>{statusLabel(w.status)}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" style={linkBtn} onClick={() => setEdit(w)}>
                        Edit
                      </button>
                      {w.status === "ACTIVE" ? (
                        <>
                          {" "}
                          <button type="button" style={linkBtn} onClick={() => deactivate(w)}>
                            Deactivate
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <WorkflowDrawer show={edit !== undefined} workflow={edit || null} onHide={() => setEdit(undefined)} onDone={reload} />
    </>
  );
}
